import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import Logo from '../Logo/Logo';
import Cart from '../Cart/Cart';
import AuthModal from '../Auth/AuthModal';
import { useToast } from '../../contexts/ToastContext';
import { isAdminUser } from '../../utils/adminUtils';
import './Header.css';

const Header = ({ onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isVerifiedUser, logout } = useAuth();
  const toast = useToast();
  const { getTotalItems, clearCart } = useCart();
  const [cartOpen, setCartOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCatalogClick = (e) => {
    e.preventDefault();
    if (onNavigate) onNavigate('catalog');
    else navigate('/catalog');
  };

  const handleAboutClick = (e) => {
    e.preventDefault();
    if (onNavigate) onNavigate('about');
    else navigate('/about');
  };

  const handleHomeClick = (e) => {
    e.preventDefault();
    if (onNavigate) onNavigate('home');
    else navigate('/');
  };

  const handleUserClick = () => {
    if (isVerifiedUser) {
      setProfileOpen((prev) => !prev);
    } else {
      setAuthModalOpen(true);
    }
  };

  const handleLogout = async () => {
    const itemCount = getTotalItems();
    const confirmed = window.confirm(
      itemCount > 0
        ? `Tem certeza que quer sair? Seu carrinho tem ${itemCount} item(ns) e será esvaziado.`
        : 'Tem certeza que quer sair da sua conta?'
    );
    if (!confirmed) return;

    setProfileOpen(false);
    setCartOpen(false);
    clearCart();
    await logout();

    if (['/checkout', '/meus-pedidos', '/admin'].includes(location.pathname)) {
      navigate('/');
    }

    toast.success('Você saiu da sua conta.');
  };

  const displayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Usuário';
  const isAdmin = isAdminUser(currentUser);

  return (
    <>
      <header className="header">
        <div className="header-container">
          <div onClick={handleHomeClick} className="header-logo-wrapper">
            <Logo className="header-logo" />
          </div>
          <nav className="nav">
            <a
              href="#catalogo"
              onClick={handleCatalogClick}
              className={`nav-link ${isActive('/catalog') ? 'active' : ''}`}
            >
              CATÁLOGO
            </a>
            <a
              href="#sobre"
              onClick={handleAboutClick}
              className={`nav-link ${isActive('/about') ? 'active' : ''}`}
            >
              SOBRE
            </a>
          </nav>
          <div className="header-actions">
            <div className="search-container">
              <input
                type="text"
                placeholder="Pesquise camisas, blusas..."
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>
            <button className="icon-button" onClick={() => setCartOpen(true)}>
              <span className="icon">🛍️</span>
              {getTotalItems() > 0 && (
                <span className="cart-badge">{getTotalItems()}</span>
              )}
            </button>
            <div className="profile-wrapper" ref={profileRef}>
              <button className="icon-button" onClick={handleUserClick}>
                <span className="icon">👤</span>
                {isVerifiedUser && <span className="user-dot" />}
              </button>
              {profileOpen && isVerifiedUser && (
                <div className="profile-dropdown">
                  <div className="profile-dropdown-name">{displayName}</div>
                  <div className="profile-dropdown-email">{currentUser.email}</div>
                  <hr className="profile-dropdown-divider" />
                  <button
                    className="profile-dropdown-item"
                    onClick={() => { setProfileOpen(false); navigate('/meus-pedidos'); }}
                  >
                    Meus Pedidos
                  </button>
                  {isAdmin && (
                    <button
                      className="profile-dropdown-item profile-dropdown-item--admin"
                      onClick={() => { setProfileOpen(false); navigate('/admin'); }}
                    >
                      Painel Admin
                    </button>
                  )}
                  <button className="profile-dropdown-logout" onClick={handleLogout}>
                    Sair
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <Cart isOpen={cartOpen} onClose={() => setCartOpen(false)} />
      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </>
  );
};

export default Header;
